import ensureError from "@-/com.npmjs.ensure-error";
import { SuperwallProvider, useUser } from "@-/com.npmjs.expo-superwall";
import { LogBox, Platform } from "@-/com.npmjs.react-native";
import { LaunchArguments } from "@-/com.npmjs.react-native-launch-arguments";
import { getAuthClient } from "@-/dev.improve.haniverse.lib.auth.client";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import onetime from "@-/com.npmjs.onetime";
import { type ReactNode, useEffect, useRef } from "react";
import {
  getSuperwallTestModeBehavior,
  getSuperwallTestModeFallbackProducts,
  getSuperwallTestModePaywallOptions,
} from "#get-superwall-test-mode-behavior";
import { SuperwallAnalyticsBridge } from "#superwall-analytics-bridge";

const superwallApiKeys = {
  android: process.env.EXPO_PUBLIC_SUPERWALL_ANDROID_API_KEY,
  ios: process.env.EXPO_PUBLIC_SUPERWALL_IOS_API_KEY,
};

// Local dev / CI dev-clients ship no EXPO_PUBLIC_SUPERWALL_*_API_KEY, and the SDK
// `console.error`s the missing key on EVERY provider mount (SuperwallProvider.js:
// "[Superwall] Configure failed … No API key provided"), with our
// `handleConfigurationError` echoing it. In dev builds each of those becomes a
// LogBox toast pinned over the screen's BOTTOM edge — exactly where the auth
// guest gate's "Continue without an account" link and the confirm dialog's terms
// checkbox sit — so the toast swallows taps and hides those elements from the
// accessibility tree. That single overlay killed 29/33 ios Maestro flows in run
// 30515543191 (every guest-gate flow died on `auth-guest-terms-checkbox`; the 4
// sign-in flows passed) and eats real taps in manual dev too. A missing key is
// EXPECTED configuration for these builds, not an error — so our handler skips
// its error log and the SDK's one hardcoded `console.error` for the missing key
// is swallowed by a scoped console wrapper, both ONLY when the key is genuinely
// absent (a present but misconfigured key still toasts loudly).
// `LogBox.ignoreLogs` alone cannot do this: the patched expo log-box surface
// does not honor its patterns (verified live — the toast survives a cold launch
// with the ignore installed before any render). (Cannot skip the provider
// instead: `useSuperwall` throws outside it, which would crash the premium
// hooks.)
// `?? ""` + trim: varlock inlines declared-but-unset EXPO_PUBLIC vars as EMPTY
// STRINGS, not undefined — the SDK's own `if (!apiKey)` treats "" as missing,
// so this check must too or the guard silently never engages.
function isSuperwallKeyMissing(): boolean {
  return (
    Platform.OS !== "web" &&
    ((Platform.OS === "android" ? superwallApiKeys.android : superwallApiKeys.ios) ?? "").trim() ===
      ""
  );
}

const SUPERWALL_SDK_CONFIGURE_FAILED_REGEX = /\[Superwall\] Configure failed/u;
const SUPERWALL_GATE_CONFIGURE_FAILED_REGEX = /Superwall configuration failed/u;

// Installed from SuperwallGate's render (before the provider's configure effect
// can fire) rather than at module top level; `onetime` keeps it idempotent.
const installMissingSuperwallKeyLogFilter = onetime(() => {
  LogBox.ignoreLogs([SUPERWALL_SDK_CONFIGURE_FAILED_REGEX, SUPERWALL_GATE_CONFIGURE_FAILED_REGEX]);
  // oxlint-disable-next-line no-console -- scoped filter for the SDK's hardcoded emission
  const originalConsoleError = console.error.bind(console);
  // oxlint-disable-next-line no-console -- scoped filter for the SDK's hardcoded emission
  console.error = (...args: unknown[]) => {
    if (typeof args[0] === "string" && args[0].startsWith("[Superwall] Configure failed")) {
      logger(import.meta.location).info(
        "Superwall not configured (no API key for this build); premium features disabled",
      );
      return;
    }
    originalConsoleError(...args);
  };
});

function isPurchaseSimulationRequested(): boolean {
  if (process.env.EXPO_PUBLIC_APP_VARIANT !== "staging") {
    return false;
  }
  try {
    const { hanSimulatePurchases } = LaunchArguments.value<{ hanSimulatePurchases?: unknown }>();
    return hanSimulatePurchases === true || hanSimulatePurchases === "true";
  } catch {
    // Web and Node-only tests have no native launch-arguments module.
    return false;
  }
}

function getSuperwallOptions() {
  const simulatePurchases = isPurchaseSimulationRequested();
  const testModeFallbackProducts = getSuperwallTestModeFallbackProducts({
    appVariant: process.env.EXPO_PUBLIC_APP_VARIANT,
    simulatePurchases,
  });
  const testModePaywallOptions = getSuperwallTestModePaywallOptions({
    appVariant: process.env.EXPO_PUBLIC_APP_VARIANT,
    simulatePurchases,
  });
  return {
    passIdentifiersToPlayStore: true,
    shouldObservePurchases: true,
    testModeBehavior: getSuperwallTestModeBehavior({
      appVariant: process.env.EXPO_PUBLIC_APP_VARIANT,
      forceTestMode: process.env.EXPO_PUBLIC_SUPERWALL_FORCE_TEST_MODE,
      isDevelopmentBundle: __DEV__,
      simulatePurchases,
    }),
    ...(testModeFallbackProducts === undefined || testModePaywallOptions === undefined
      ? {}
      : {
          // Fallback StoreProducts are installed after configuration. Avoid caching an empty-
          // product paywall before that install; the genuine placement request will own them.
          paywalls: testModePaywallOptions,
          testModeFallbackProducts,
        }),
  } as const;
}

function handleConfigurationError(error: Error): void {
  // The missing-key case is expected build configuration (see the block above) —
  // the wrapper already logged it at info level; an error toast here would put
  // the LogBox overlay right back on top of the auth gate.
  if (isSuperwallKeyMissing()) {
    return;
  }
  logger(import.meta.location).error("Superwall configuration failed", error);
}

function SuperwallIdentitySync() {
  const { data: session } = getAuthClient().useSession();
  const { identify, signOut, user } = useUser();
  const previousAuthUserIdRef = useRef<string | null | undefined>(undefined);
  const authUserId = session?.user.id ?? null;

  useEffect(() => {
    const previousAuthUserId = previousAuthUserIdRef.current;
    previousAuthUserIdRef.current = authUserId;

    if (authUserId !== null) {
      if (user?.appUserId === authUserId) {
        return;
      }
      void identify(authUserId, { restorePaywallAssignments: true }).catch((error: unknown) => {
        logger(import.meta.location).error("Superwall identify failed", ensureError(error));
      });
      return;
    }

    // Preserve Superwall's stable anonymous identity on ordinary signed-out launches.
    // Reset only when Better Auth transitions from an authenticated user to signed out.
    if (previousAuthUserId !== null && previousAuthUserId !== undefined) {
      void signOut().catch((error: unknown) => {
        logger(import.meta.location).error("Superwall sign-out failed", ensureError(error));
      });
    }
  }, [authUserId, identify, signOut, user?.appUserId]);

  return null;
}

export function SuperwallGate({ children }: { children: ReactNode }) {
  if (Platform.OS === "web") {
    return children;
  }

  if (isSuperwallKeyMissing()) {
    installMissingSuperwallKeyLogFilter();
  }

  return (
    <SuperwallProvider
      apiKeys={superwallApiKeys}
      onConfigurationError={handleConfigurationError}
      options={getSuperwallOptions()}
    >
      {/* identify()/signOut() call into the native module, and every native Superwall entry
          point dereferences `Superwall.shared` — an assertionFailure (SIGTRAP in debug builds)
          when `configure` never ran. On keyless builds Superwall never configures, so a signed-in
          session mounting this sync would crash the app; skip it entirely. */}
      {isSuperwallKeyMissing() ? null : (
        <>
          <SuperwallIdentitySync />
          <SuperwallAnalyticsBridge />
        </>
      )}
      {children}
    </SuperwallProvider>
  );
}
